import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Clock, Loader2, ShieldCheck, ShieldOff, X } from "lucide-react";
import api, { formatError } from "@/lib/api";

export const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };
export const remaining = (iso, nowMs = Date.now()) => {
  const ms = new Date(iso).getTime() - nowMs;
  if (!(ms > 0)) return "0m";
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return h ? `${h}h ${m}m` : `${m}m`;
};
const DURATIONS = [
  ["2h", "2 hours", "Automatically turns back ON after 2 hours."],
  ["24h", "24 hours", "Automatically turns back ON after 24 hours."],
  ["manual", "Until I turn it ON manually", "Stays OFF with no scheduled expiry."],
];
export const actorLabel = (email, label) => (label ? `${email} (${label})` : email);
const DURATION_TEXT = { "2h": "2 hours", "24h": "24 hours", manual: "until manually switched ON" };

function AuditList({ rows }) {
  if (!rows) return null;
  return (
    <div className="mt-2 border-t border-line pt-2" data-testid="otp-audit-list">
      
      {rows.length === 0 ? <p className="text-[11px] text-muted2 mt-1">No changes recorded yet.</p> : (
        <ul className="mt-1 space-y-1">
          {rows.slice(0, 8).map((r) => (
            <li key={r.id} className="text-[11px] text-muted2" data-testid="otp-audit-row">
              <span className="text-ink">{fmtWhen(r.at)}</span> · {r.action === "auto_on" ? "Automatically switched ON (timed OFF expired)" : r.action === "manual_on" ? "Switched ON" : `Switched OFF for ${DURATION_TEXT[r.duration] || r.duration}`}
              {r.action !== "auto_on" && r.by ? <> · by {actorLabel(r.by, r.by_label)}</> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Shared poller: every staff account reads the same global setting (no local caching). */
export function useOtpSetting(intervalMs = 30000) {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const load = async () => {
    try { const { data } = await api.get("/admin/settings/otp-verification"); setState(data); setError(""); }
    catch (e) { setError(formatError(e) || "Could not load the OTP setting."); }
  };
  useEffect(() => { load(); const t = setInterval(load, intervalMs); return () => clearInterval(t); }, [intervalMs]);
  return { state, error, reload: load, setState };
}

export function OtpDisabledBanner({ state, onRestore }) {
  const [, tick] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 60000); return () => clearInterval(t); }, []);
  if (!state || state.required !== false) return null;
  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/otp-verification", { required: true });
      onRestore?.(data); toast.success("OTP verification is now ON for Sider and Bridal.");
    } catch (e) { toast.error(formatError(e) || "Could not turn OTP ON. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="otp-disabled-banner" role="alert" className="mb-4 rounded-2xl border border-[#8A2A2A]/40 bg-[#F5D3D3] px-4 py-3 text-[#5A1A1A] shadow-[0_10px_30px_-18px_rgba(138,42,42,0.6)]">
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-[#8A2A2A]" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A2A2A]">⚠️ OTP Temporarily Disabled</p>
          <p className="text-sm mt-1 leading-relaxed">Mobile number verification is currently non-compulsory for all customer submissions across Sider and Bridal.</p>
          <p className="text-xs mt-1.5 text-[#8A2A2A]" data-testid="otp-disabled-banner-detail">
            {state.off_until
              ? <>Remaining: <span className="font-semibold">{remaining(state.off_until)}</span> · automatically resumes {fmtWhen(state.off_until)}</>
              : <span className="font-semibold">Manual reactivation required</span>}
            {state.disabled_by ? ` · disabled by ${actorLabel(state.disabled_by, state.disabled_by_label)}${state.disabled_at ? ` on ${fmtWhen(state.disabled_at)}` : ""}` : ""}
          </p>
        </div>
        <button type="button" onClick={restore} disabled={busy} data-testid="otp-banner-restore-btn"
          className="flex-shrink-0 self-center inline-flex items-center gap-1.5 rounded-full bg-[#2D8A3E] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:brightness-95 transition-[filter] disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} strokeWidth={2} />} Turn ON now
        </button>
      </div>
    </div>
  );
}

function DurationDialog({ onPick, onClose, busy }) {
  const [choice, setChoice] = useState("2h");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={onClose} data-testid="otp-off-dialog">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div><p className="overline text-[10px]">Confirm</p><h3 className="font-serif text-xl text-ink mt-1">Turn OTP verification OFF?</h3></div>
          <button type="button" onClick={onClose} className="text-muted2 hover:text-maroon" data-testid="otp-off-dialog-close"><X size={18} /></button>
        </div>
        <p className="text-xs text-muted2 mt-2 leading-relaxed">This applies to every customer form across Sider and Bridal. How long should it stay OFF?</p>
        <div className="mt-4 space-y-2" role="radiogroup">
          {DURATIONS.map(([v, l, d]) => (
            <button key={v} type="button" role="radio" aria-checked={choice === v} data-testid={`otp-off-${v}`} onClick={() => setChoice(v)}
              className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${choice === v ? "border-maroon bg-[#FAF7F2]" : "border-line hover:border-maroon/40"}`}>
              <p className="text-sm text-ink">{l}</p><p className="text-[11px] text-muted2">{d}</p>
            </button>
          ))}
        </div>
        <div className="flex gap-2 pt-4">
          <button type="button" onClick={onClose} className="btn-outline-maroon flex-1 !py-2 !text-xs">Cancel</button>
          <button type="button" disabled={busy} data-testid="otp-off-confirm" onClick={() => onPick(choice)} className="btn-maroon flex-1 !py-2 !text-xs inline-flex items-center justify-center gap-1.5">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldOff size={13} />} Turn OFF
          </button>
        </div>
      </div>
    </div>
  );
}

function Switch({ on, busy, onChange }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label="Mobile Number OTP Verification" disabled={busy} onClick={onChange}
      data-testid="otp-setting-switch"
      className={`relative inline-flex h-7 w-[52px] flex-shrink-0 items-center rounded-full border transition-colors duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#2D4A22]/50 disabled:opacity-60 ${on ? "bg-[#2D8A3E] border-[#2D8A3E]" : "bg-[#E6E1DA] border-[#D6D0C6]"}`}>
      <span aria-hidden className={`absolute left-0 top-0 h-full w-full flex items-center justify-between px-1.5 text-[8px] font-semibold uppercase tracking-wider ${on ? "text-white/90" : "text-muted2"}`}>
        <span className={on ? "opacity-100" : "opacity-0"}>On</span><span className={on ? "opacity-0" : "opacity-100"}>Off</span>
      </span>
      <span data-testid="otp-setting-knob" className={`relative h-5 w-5 rounded-full bg-white shadow-md transform transition-transform duration-300 ease-out ${on ? "translate-x-[27px]" : "translate-x-[3px]"}`} />
    </button>
  );
}

export default function OtpVerificationSetting({ otp, showHistory: historyDefault = false }) {
  const { state, error, reload, setState } = otp;
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [showHistory, setShowHistory] = useState(historyDefault);
  const [audit, setAudit] = useState(null);
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 60000); return () => clearInterval(t); }, []);
  const loadAudit = () => api.get("/admin/settings/otp-verification/audit").then(({ data }) => setAudit(data)).catch(() => {});
  useEffect(() => { if (showHistory) loadAudit(); }, [showHistory, state?.updated_at, state?.last_event?.at]);

  const save = async (body, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/otp-verification", body);
      setState(data); setDialog(false); toast.success(okMsg);
    } catch (e) { toast.error(formatError(e) || "The setting could not be saved. The previous value is still in effect."); reload(); }
    finally { setBusy(false); }
  };
  const on = state?.required === true;
  const autoResumed = on && state?.last_event?.action === "auto_on";
  const onToggle = () => on ? setDialog(true) : save({ required: true }, "OTP verification is now ON for Sider and Bridal.");

  return (
    <section data-testid="otp-setting-card" className="rounded-2xl border border-line bg-white px-4 py-3 mb-4">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm text-ink font-medium leading-tight">Mobile Number OTP Verification</h3>
          <p className="text-[11px] text-muted2 mt-0.5 truncate" data-testid="otp-setting-status">
            {error ? <span className="text-[#8A2A2A]" data-testid="otp-setting-error">{error}</span>
            : !state ? "Loading…"
            : <>
                <span className={`font-medium ${on ? "text-[#2D8A3E]" : "text-[#8A2A2A]"}`} data-testid="otp-setting-current">{on ? "ON" : "OFF"}</span>
                <span> · Sider + Bridal</span>
                {!on && (state.off_until
                  ? <span data-testid="otp-setting-off-detail"> · resumes {fmtWhen(state.off_until)} ({remaining(state.off_until)} left)</span>
                  : <span data-testid="otp-setting-off-detail"> · manual reactivation required</span>)}
                {!on && state.disabled_by ? <span> · by {actorLabel(state.disabled_by, state.disabled_by_label)}</span> : null}
                {autoResumed && <span data-testid="otp-setting-auto-resumed"> · auto-resumed {fmtWhen(state.last_event.at)}</span>}
              </>}
          </p>
        </div>
        {state && !error && <Switch on={on} busy={busy} onChange={onToggle} />}
      </div>
      <button type="button" onClick={() => setShowHistory((v) => !v)} data-testid="otp-history-toggle"
        className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-muted2 hover:text-maroon inline-flex items-center gap-1">
        <Clock size={10} /> {showHistory ? "Hide history" : "History"}
      </button>
      {showHistory && <AuditList rows={audit} />}
      {dialog && <DurationDialog busy={busy} onClose={() => setDialog(false)} onPick={(d) => save({ required: false, duration: d }, d === "manual" ? "OTP verification is OFF until you turn it ON manually." : `OTP verification is OFF for ${d === "2h" ? "2 hours" : "24 hours"} and will resume automatically.`)} />}
    </section>
  );
}
